import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationAssaysModal from '@/modules/configurationModule/detail/assays/configuration-assays/components/configuration-assays-modal/configuration-assays-modal.vue'
import ConfigurationAssaysModalComp from '@/modules/configurationModule/detail/assays/configuration-assays/components/configuration-assays-modal/configuration-assays-modal'
import CodificationSearcherModal from '@/modules/configurationModule/detail/samples/CodificationSearcherModal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import { ConfigurationAssaysGridState, initialConfigurationAssaysGridState } from '@/modules/configurationModule/detail/assays/configuration-assays/components/configuration-assays-grid/configuration-assays-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { ConfigurationAssayResponse, ConfigurationAssayCreateRequest, ConfigurationAssayPatchInBatchRequest } from '@/modules/configurationModule/detail/assays/configuration-assays/services/configuration-assay/configuration-assay.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationAssaysModal,
        CodificationSearcherModal
    }
})
export default class ConfigurationAssaysGrid extends BaseCrudServerSideComponent<ConfigurationAssayResponse> implements BaseCrud {
    @Ref() readonly configurationAssaysModalWrapper!: typeof ModalWrapper
    @Ref() readonly codificationSearcherModalWrapper!: typeof ModalWrapper
    @Ref() readonly configurationAssaysModal!: ConfigurationAssaysModalComp

    state: ConfigurationAssaysGridState

    get canUpdate(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationAssaysGridState()
    }

    /// SERVICES CALLS ///

    private async _createAssay(createRequest: ConfigurationAssayCreateRequest): Promise<ConfigurationAssayResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.configurationAssaysModal.hide()
            return response
        }
        return null
    }

    private async _patchAssays(batchRequests: ConfigurationAssayPatchInBatchRequest[]): Promise<EntityIdentifierWithErrorResponse[] | null> {
        if (batchRequests) {
            const response = await this.$_toCallUnique(this.state.service.patch(batchRequests))
            if (response) {
                const elements = batchRequests.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
                    this.configurationAssaysModal.hide()
                }
                return response
            }
        }
        return null
    }

    private async _deleteAssays(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async activateDeactivateSelection(active: boolean, assays: ConfigurationAssayResponse[]): Promise<void> {
        if (assays?.length) {
            const batchRequests = assays.filter(assay => assay.active !== active)
                .map(assay => ({
                    id: assay.id,
                    active
                }))
            if (await this._patchAssays(batchRequests)) {
                this.reloadData()
            }
        }
    }

    async createOperation(createRequest: ConfigurationAssayCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createAssay(createRequest)) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async patchOperation(batchRequest: ConfigurationAssayPatchInBatchRequest): Promise<void> {
        if (batchRequest) {
            if (await this._patchAssays([batchRequest])) {
                this.reloadData()
            }
        }
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteAssays(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    onUpdate(assay: ConfigurationAssayResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        if (assay) {
            this.showModal(this.$tc('configuration.actions.update'), assay, this.configurationAssaysModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(assay => assay.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.configurationAssaysModalWrapper)
    }

    onSearch(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.codificationSearcherModalWrapper)
    }

    async onActivate(): Promise<void> {
        await this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    async onDeactivate(): Promise<void> {
        await this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: params?.node?.data?.active ? this.$tc('configuration.actions.deactivate') : this.$tc('configuration.actions.activate'),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$tc('configuration.actions.activate'),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: this.onActivate
            },
            {
                name: this.$tc('configuration.actions.deactivate'),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: this.onDeactivate
            }
        ]
    }
}
